import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Login from "./pages/auth/Login";
import SignUp from "./pages/auth/SignUp";
import ForgotPassword from "./pages/auth/ForgotPassword";
// Import patient dashboard
import PatientDashboard from "./pages/patient/PatientDashboard";
import PatientCheckout from "./pages/patient/PatientCheckout";
// Import admin components
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminInvoices from "./pages/admin/AdminInvoices";
import AdminAccounts from "./pages/admin/AdminAccounts";
import AdminAccountDetails from "./pages/admin/AdminAccountDetails";
import PatientConsents from "./pages/patient/PatientConsents";
import { WhiteLabelProvider } from "./contexts/WhiteLabelContext";

// Protected Route Component
const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  allowedRoles?: string[];
}> = ({ children, allowedRoles }) => {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};

// Placeholder components for now
const Dashboard = () => <div className="p-8">Dashboard</div>;
const SignupSuccess = () => <div className="p-8">Signup Success</div>;
const Unauthorized = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
      <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg
          className="h-8 w-8 text-red-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
          />
        </svg>
      </div>
      <h2 className="text-xl font-bold text-gray-900 mb-2">
        Unauthorized Access
      </h2>
      <p className="text-gray-600 mb-6">
        You don't have permission to access this page.
      </p>
      <button
        onClick={() => (window.location.href = "/login")}
        className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
      >
        Back to Login
      </button>
    </div>
  </div>
);

function App() {
  return (
    <WhiteLabelProvider>
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/signup-success" element={<SignupSuccess />} />
            <Route path="/unauthorized" element={<Unauthorized />} />

            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />

            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <Navigate to="/admin/dashboard" replace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/products"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminProducts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminUsers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/orders"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminOrders />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/invoices"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminInvoices />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/accounts"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminAccounts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/accounts/:accountId"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminAccountDetails />
                </ProtectedRoute>
              }
            />

            {/* Clinic Routes */}
            <Route
              path="/account"
              element={
                <ProtectedRoute allowedRoles={["clinic"]}>
                  <Navigate to="/account/dashboard" replace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/account/dashboard"
              element={
                <ProtectedRoute allowedRoles={["clinic"]}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/account/orders"
              element={
                <ProtectedRoute allowedRoles={["clinic"]}>
                  <AdminOrders />
                </ProtectedRoute>
              }
            />
            <Route
              path="/account/accounts"
              element={
                <ProtectedRoute allowedRoles={["clinic"]}>
                  <AdminAccounts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/account/accounts/:accountId"
              element={
                <ProtectedRoute allowedRoles={["clinic"]}>
                  <AdminAccountDetails />
                </ProtectedRoute>
              }
            />

            {/* Patient Routes */}
            <Route
              path="/patient/*"
              element={
                // <ProtectedRoute allowedRoles={['patient']}>
                <Routes>
                  <Route path="dashboard" element={<PatientDashboard />} />
                  <Route path="checkout" element={<PatientCheckout />} />
                  <Route path="consents" element={<PatientConsents />} />
                  <Route
                    path="/"
                    element={<Navigate to="/patient/dashboard" replace />}
                  />
                </Routes>
                // </ProtectedRoute>
              }
            />

            {/* Default Route */}
            <Route path="/" element={<Navigate to="/login" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </WhiteLabelProvider>
  );
}

export default App;
